/**
 * Field diamond — SVG visualization for one game.
 *
 * Layout (viewBox 0 0 100 100):
 *   - bases: 2B at (50, 30), 1B at (70, 50), home at (50, 70), 3B at (30, 50)
 *   - pitcher's mound at (50, 50)
 *   - outfield arc filled with a soft grass gradient
 *
 * Each fielder badge shows jersey # (primary) + player initials. Hovering
 * or focusing a badge opens a Tooltip with photo + name + this-game line.
 * Click opens the full PlayerModal. Runners render as inset team-color
 * dots on the bases they occupy — the base itself stays neutral.
 */
import { useCallback, useState } from 'react';
import type {
  AtBatHit,
  BatterOnFieldInfo,
  FielderInfo,
  FieldersState,
  LastAtBatResult,
  PitchEntry,
  RunnerInfo,
  RunnersState,
} from '../state/selectors';
import AtBatResultArrows from './AtBatResultArrows';
import { teamColor } from '../state/teamColors';
import FieldStrikeZone from './FieldStrikeZone';
import PitchingChangeBanner from './PitchingChangeBanner';
import type { PlayerSeed } from './PlayerModal';
import PlayerTooltipCard from './PlayerTooltipCard';
import Tooltip from './Tooltip';
import styles from './FieldDiamond.module.css';

interface FieldDiamondProps {
  fielders: FieldersState;
  runners: RunnersState;
  awayTeamCode: string;
  homeTeamCode: string;
  /**
   * Hits in the currently-displayed at-bat — every playEvent with hitData.
   * In-play hits render larger (the primary yellow "ball-landing"); fouls
   * render smaller and muted. Empty array = no ping at all.
   */
  atBatHits?: AtBatHit[];
  onPlayerClick?: (seed: PlayerSeed) => void;
  /** Current at-bat pitches for the inline strike-zone overlay (bottom-right). */
  currentAtBatPitches?: PitchEntry[];
  /** Batter at the plate — rendered as a small badge on L/R of home plate. */
  batterOnField?: BatterOnFieldInfo | null;
  /** Pitching-change banner: when set, renders a transient "Now pitching" pill. */
  pitchingChange?: { name: string; jersey: string | null; key: number } | null;
  /**
   * The most-recently-completed at-bat. When this changes (atBatIndex bump),
   * AtBatResultArrows fades in with runner-movement arrows over the field
   * for 8s. Null when no completed at-bat is yet available.
   */
  lastAtBatResult?: LastAtBatResult | null;
  /**
   * User-dismissed at-bat index (X button on the banner). When equal to
   * lastAtBatResult.atBatIndex, AtBatResultArrows hides.
   */
  dismissedAtBatIndex?: number | null;
  /**
   * Bumped by the parent's Replay button to re-fire the overlay timer after a
   * dismiss / auto-fade.
   */
  resultReplayKey?: number;
}

interface Pos {
  x: number;
  y: number;
}

interface TooltipState {
  rect: DOMRect;
  playerId: number;
  name: string;
  subtitle: string;
}

const BASE_POSITIONS: Record<'first' | 'second' | 'third' | 'home', Pos> = {
  first: { x: 70, y: 50 },
  second: { x: 50, y: 30 },
  third: { x: 30, y: 50 },
  home: { x: 50, y: 70 },
};
const FIELDER_POSITIONS: Record<keyof FieldersState, Pos> = {
  pitcher: { x: 50, y: 50 },
  catcher: { x: 50, y: 78 },
  first: { x: 65, y: 45 },
  second: { x: 57, y: 35 },
  third: { x: 35, y: 45 },
  shortstop: { x: 43, y: 35 },
  left: { x: 22, y: 22 },
  center: { x: 50, y: 12 },
  right: { x: 78, y: 22 },
};

function FielderNode({
  info,
  pos,
  awayTeamCode,
  homeTeamCode,
  onClick,
  onShowTooltip,
  onHideTooltip,
}: {
  info: FielderInfo;
  pos: Pos;
  awayTeamCode: string;
  homeTeamCode: string;
  onClick?: (seed: PlayerSeed) => void;
  onShowTooltip: (state: TooltipState) => void;
  onHideTooltip: () => void;
}): JSX.Element {
  const code = info.side === 'away' ? awayTeamCode : homeTeamCode;
  const { primary } = teamColor(code);
  const handleClick = (): void =>
    onClick?.({ id: info.id, name: info.name, jersey: info.jersey, position: info.position });
  const subtitle = `${info.position}${info.jersey ? ` · #${info.jersey}` : ''}`;
  const onEnter = (e: React.MouseEvent | React.FocusEvent): void => {
    const target = e.currentTarget as SVGGElement;
    onShowTooltip({
      rect: target.getBoundingClientRect(),
      playerId: info.id,
      name: info.name,
      subtitle,
    });
  };
  return (
    <g
      data-testid={`fielder-${info.position}`}
      className={styles.fielderGroup}
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onMouseEnter={onEnter}
      onMouseLeave={onHideTooltip}
      onFocus={onEnter}
      onBlur={onHideTooltip}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      aria-label={`${info.position} ${info.name}${info.jersey ? `, number ${info.jersey}` : ''}`}
    >
      <circle cx={pos.x} cy={pos.y} r={7} className={styles.fielderHit} />
      <circle
        cx={pos.x}
        cy={pos.y}
        r={4.5}
        className={styles.fielderRing}
        style={{ stroke: primary }}
      />
      <text
        x={pos.x}
        y={pos.y - 0.6}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={2.3}
        className={styles.fielderJersey}
        fill={primary}
      >
        {info.jersey ?? '?'}
      </text>
      <text
        x={pos.x}
        y={pos.y + 2.2}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={1.9}
        className={styles.fielderInitials}
        fill="var(--c-ink-70)"
      >
        {info.initials}
      </text>
    </g>
  );
}

function BaseSquare({
  pos,
  label,
}: {
  pos: Pos;
  label: 'home' | 'first' | 'second' | 'third';
}): JSX.Element {
  const size = 4;
  return (
    <rect
      x={pos.x - size / 2}
      y={pos.y - size / 2}
      width={size}
      height={size}
      fill="var(--c-base)"
      stroke="var(--c-base-stroke)"
      strokeWidth={0.6}
      transform={`rotate(45 ${pos.x} ${pos.y})`}
      data-testid={`base-${label}`}
      className={styles.base}
    />
  );
}

function RunnerDot({
  runner,
  pos,
  awayTeamCode,
  homeTeamCode,
  base,
  onClick,
  onShowTooltip,
  onHideTooltip,
}: {
  runner: RunnerInfo;
  pos: Pos;
  awayTeamCode: string;
  homeTeamCode: string;
  base: 'first' | 'second' | 'third';
  onClick?: (seed: PlayerSeed) => void;
  onShowTooltip: (state: TooltipState) => void;
  onHideTooltip: () => void;
}): JSX.Element {
  const code = runner.side === 'away' ? awayTeamCode : homeTeamCode;
  const { primary } = teamColor(code);
  const handleClick = (): void =>
    onClick?.({ id: runner.id, name: runner.name, jersey: runner.jersey, position: null });
  const onEnter = (e: React.MouseEvent | React.FocusEvent): void => {
    const target = e.currentTarget as SVGGElement;
    onShowTooltip({
      rect: target.getBoundingClientRect(),
      playerId: runner.id,
      name: runner.name,
      subtitle: `Runner · ${base === 'first' ? '1st' : base === 'second' ? '2nd' : '3rd'}`,
    });
  };
  return (
    <g
      data-testid={`runner-${base}`}
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onMouseEnter={onEnter}
      onMouseLeave={onHideTooltip}
      onFocus={onEnter}
      onBlur={onHideTooltip}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      aria-label={`Runner on ${base}: ${runner.name}`}
      className={styles.runnerGroup}
    >
      <circle
        cx={pos.x}
        cy={pos.y}
        r={2.6}
        className={styles.runnerDot}
        fill={primary}
        stroke="var(--c-ink-0)"
        strokeWidth={0.4}
      />
      <text
        x={pos.x}
        y={pos.y + 0.5}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={1.6}
        fill="var(--c-ink-0)"
        className={styles.runnerInitials}
      >
        {runner.initials}
      </text>
      {runner.jersey && (
        <text
          x={pos.x}
          y={pos.y + 5.2}
          textAnchor="middle"
          fontSize={2}
          fontWeight={700}
          fill="var(--c-ink-100, #0c111c)"
          stroke="var(--c-ink-0)"
          strokeWidth={0.5}
          paintOrder="stroke fill"
          className={styles.runnerJersey}
        >
          #{runner.jersey}
        </text>
      )}
    </g>
  );
}

export default function FieldDiamond({
  fielders,
  runners,
  awayTeamCode,
  homeTeamCode,
  atBatHits = [],
  onPlayerClick,
  currentAtBatPitches = [],
  batterOnField = null,
  pitchingChange = null,
  lastAtBatResult = null,
  dismissedAtBatIndex = null,
  resultReplayKey = 0,
}: FieldDiamondProps): JSX.Element {
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const showTooltip = useCallback((s: TooltipState) => setTooltip(s), []);
  const hideTooltip = useCallback(() => setTooltip(null), []);

  // Pings stay rendered for the lifetime of the selected at-bat. When the
  // user navigates to a different batter the parent passes a new hits array
  // and the SVG <g> remounts (keyed below), restarting the fade-in.
  const hitsKey = atBatHits.map((h) => h.hitId).join('|') || 'no-hits';
  const primaryHitIndex = (() => {
    for (let i = atBatHits.length - 1; i >= 0; i--) {
      if (atBatHits[i]!.isInPlay) return i;
    }
    return -1;
  })();

  return (
    <div className={styles.wrap}>
      <svg
        viewBox="0 0 100 100"
        role="img"
        aria-label="Field diamond"
        data-testid="field-diamond"
        className={styles.svg}
      >
        <defs>
          <radialGradient id="grass-grad" cx="50%" cy="60%" r="80%">
            <stop offset="0%" stopColor="var(--c-grass-1)" />
            <stop offset="55%" stopColor="var(--c-grass-2)" />
            <stop offset="100%" stopColor="var(--c-grass-3)" />
          </radialGradient>
          <linearGradient id="dirt-grad" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="var(--c-dirt-1)" />
            <stop offset="100%" stopColor="var(--c-dirt-2)" />
          </linearGradient>
        </defs>

        <path
          d="M 2 64 Q 50 -16 98 64 L 98 88 L 2 88 Z"
          fill="url(#grass-grad)"
          stroke="var(--c-grass-3)"
          strokeWidth={0.3}
        />

        <line x1={50} y1={70} x2={2} y2={22} stroke="var(--c-ink-10)" strokeWidth={0.3} opacity={0.8} />
        <line x1={50} y1={70} x2={98} y2={22} stroke="var(--c-ink-10)" strokeWidth={0.3} opacity={0.8} />

        <polygon
          points={`${BASE_POSITIONS.home.x},${BASE_POSITIONS.home.y} ${BASE_POSITIONS.first.x},${BASE_POSITIONS.first.y} ${BASE_POSITIONS.second.x},${BASE_POSITIONS.second.y} ${BASE_POSITIONS.third.x},${BASE_POSITIONS.third.y}`}
          fill="url(#dirt-grad)"
          stroke="var(--c-dirt-stroke)"
          strokeWidth={0.35}
        />

        <circle cx={50} cy={50} r={4.5} fill="url(#dirt-grad)" stroke="var(--c-dirt-stroke)" strokeWidth={0.25} />

        <BaseSquare pos={BASE_POSITIONS.home} label="home" />
        <BaseSquare pos={BASE_POSITIONS.first} label="first" />
        <BaseSquare pos={BASE_POSITIONS.second} label="second" />
        <BaseSquare pos={BASE_POSITIONS.third} label="third" />

        {(Object.keys(FIELDER_POSITIONS) as Array<keyof FieldersState>).map((key) => {
          const info = fielders[key];
          if (!info) return null;
          return (
            <FielderNode
              key={key}
              info={info}
              pos={FIELDER_POSITIONS[key]}
              awayTeamCode={awayTeamCode}
              homeTeamCode={homeTeamCode}
              onClick={onPlayerClick}
              onShowTooltip={showTooltip}
              onHideTooltip={hideTooltip}
            />
          );
        })}

        {(['first', 'second', 'third'] as const).map((base) => {
          const r = runners[base];
          if (!r) return null;
          return (
            <RunnerDot
              key={base}
              runner={r}
              pos={BASE_POSITIONS[base]}
              awayTeamCode={awayTeamCode}
              homeTeamCode={homeTeamCode}
              base={base}
              onClick={onPlayerClick}
              onShowTooltip={showTooltip}
              onHideTooltip={hideTooltip}
            />
          );
        })}

        {atBatHits.length > 0 && (
          <g key={hitsKey} className={styles.ballLanding} data-testid="ball-landing-group">
            {atBatHits.map((h, idx) => {
              const isPrimary = idx === primaryHitIndex;
              const glowR = isPrimary ? 3.5 : 2.4;
              const dotR = isPrimary ? 1.6 : 1.0;
              const dotFill = h.isInPlay
                ? 'var(--c-ball-landing, #facc15)'
                : 'var(--c-ink-50)';
              const labelDy = isPrimary ? -4.4 : -3.2;
              return (
                <g
                  key={h.hitId}
                  data-testid="ball-landing-dot"
                  data-in-play={h.isInPlay ? 'true' : 'false'}
                  data-primary={isPrimary ? 'true' : 'false'}
                  opacity={h.isInPlay ? 1 : 0.7}
                >
                  <circle
                    cx={h.x}
                    cy={h.y}
                    r={glowR}
                    fill={
                      isPrimary
                        ? 'var(--c-ball-landing-glow, rgba(250, 204, 21, 0.28))'
                        : 'rgba(0, 0, 0, 0.18)'
                    }
                  />
                  <circle
                    cx={h.x}
                    cy={h.y}
                    r={dotR}
                    fill={dotFill}
                    stroke="var(--c-ink-0)"
                    strokeWidth={0.25}
                  />
                  {h.displayNumber > 0 && (
                    <text
                      x={h.x}
                      y={h.y + labelDy}
                      textAnchor="middle"
                      fontSize={isPrimary ? 2.6 : 2.1}
                      fontWeight={700}
                      fill="var(--c-ink-100, #0c111c)"
                      stroke="var(--c-ink-0)"
                      strokeWidth={0.45}
                      paintOrder="stroke fill"
                    >
                      {h.displayNumber}
                    </text>
                  )}
                </g>
              );
            })}
          </g>
        )}

        {/* Batter at the plate — small badge offset L or R of home based on active side. */}
        {batterOnField && (
          <g data-testid="batter-on-field" pointerEvents="none">
            {(() => {
              const dx = batterOnField.activeSide === 'L' ? -4.5 : 4.5;
              const cx = 50 + dx;
              const cy = 72;
              const colors = teamColor(batterOnField.side === 'away' ? awayTeamCode : homeTeamCode);
              return (
                <>
                  <circle cx={cx} cy={cy} r={2.6} fill={colors.primary} stroke="var(--c-ink-0)" strokeWidth={0.4} />
                  <text
                    x={cx}
                    y={cy + 0.9}
                    textAnchor="middle"
                    fontSize={2}
                    fill="var(--c-ink-0)"
                    fontWeight={700}
                  >
                    {batterOnField.initials}
                  </text>
                  {batterOnField.jersey && (
                    <text
                      x={cx}
                      y={cy + 5.2}
                      textAnchor="middle"
                      fontSize={2}
                      fontWeight={700}
                      fill="var(--c-ink-100, #0c111c)"
                      stroke="var(--c-ink-0)"
                      strokeWidth={0.5}
                      paintOrder="stroke fill"
                    >
                      #{batterOnField.jersey}
                    </text>
                  )}
                </>
              );
            })()}
          </g>
        )}

        {/* Live strike zone overlay (bottom-right of the field). */}
        <FieldStrikeZone pitches={currentAtBatPitches} />
      </svg>
      {pitchingChange && (
        <PitchingChangeBanner
          pitcherName={pitchingChange.name}
          jersey={pitchingChange.jersey}
          triggerKey={pitchingChange.key}
        />
      )}
      {lastAtBatResult && (
        <AtBatResultArrows
          key={`${lastAtBatResult.atBatIndex}-${resultReplayKey}`}
          result={lastAtBatResult}
          dismissedAtBatIndex={dismissedAtBatIndex}
          replayKey={resultReplayKey}
        />
      )}
      <Tooltip
        open={tooltip !== null}
        anchorRect={tooltip?.rect ?? null}
        onRequestClose={hideTooltip}
      >
        {tooltip && (
          <PlayerTooltipCard
            personId={tooltip.playerId}
            name={tooltip.name}
            subtitle={tooltip.subtitle}
            onOpenProfile={() => {
              const seed: PlayerSeed = {
                id: tooltip.playerId,
                name: tooltip.name,
              };
              hideTooltip();
              onPlayerClick?.(seed);
            }}
          />
        )}
      </Tooltip>
    </div>
  );
}
